import { Fragment, useMemo } from 'react'
import { Box, Divider, Typography, styled } from '@mui/material'

import { IProductEligibilityRule } from '@/types'
import { DataTable } from '@/components/UI/DataTable'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { formatText } from './format'

// Rules the service sent without a category still have to be visible, so they
// collect under one trailing section rather than being dropped.
const UNCATEGORISED = 'Uncategorised'

// Check ID is what support quotes when raising a rule, so within a section the
// rules run in Check ID order rather than the order the service happened to
// send them. Numeric-aware so INCOME_2 precedes INCOME_10; rules the service
// sent without one sink to the bottom instead of leading the section.
const byCheckId = (a: IProductEligibilityRule, b: IProductEligibilityRule) => {
  const left = a.checkId?.trim() ?? ''
  const right = b.checkId?.trim() ?? ''

  if (!left || !right) {
    if (!left && !right) return 0
    return left ? -1 : 1
  }

  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' })
}

// Category is the section heading, so it is not repeated as a column. Check ID
// leads: it is the key support quotes when raising a rule with the credit team,
// so it is the first thing scanned rather than something found mid-row.
//
// Widths are fixed percentages summing to 100 so a long description wraps inside
// its own column instead of stretching the table past the panel.
const ruleColumns: ITableColumn<IProductEligibilityRule>[] = [
  {
    key: 'checkId',
    id: 'checkId',
    width: '13%',
    label: 'Check ID',
    render: (value: unknown) => <StrongText>{formatText(value)}</StrongText>,
  },
  {
    key: 'ruleId',
    id: 'ruleId',
    width: '17%',
    label: 'Rule',
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'subcategory',
    id: 'subcategory',
    width: '14%',
    label: 'Subcategory',
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'threshold',
    id: 'threshold',
    width: '10%',
    label: 'Threshold',
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'thresholdDataType',
    id: 'thresholdDataType',
    width: '10%',
    label: 'Data Type',
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'description',
    id: 'description',
    width: '28%',
    label: 'Description',
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'version',
    id: 'version',
    width: '8%',
    label: 'Version',
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
]

// Sections follow the order the categories first appear in the payload, which
// is the order the service chose; uncategorised rules always sit last. The loan
// application page lists its categories explicitly because that set is fixed
// and known — product rule categories are whatever the service configured, so
// they are derived here instead.
const groupByCategory = (rules: IProductEligibilityRule[]) => {
  const groups = new Map<string, IProductEligibilityRule[]>()

  for (const rule of rules) {
    const category = rule.category?.trim() ? rule.category : UNCATEGORISED
    const existing = groups.get(category)
    if (existing) {
      existing.push(rule)
    } else {
      groups.set(category, [rule])
    }
  }

  return [...groups.entries()]
    .map(([category, categoryRules]): [string, IProductEligibilityRule[]] => [
      category,
      [...categoryRules].sort(byCheckId),
    ])
    .sort(([a], [b]) => {
      if (a === UNCATEGORISED) return 1
      if (b === UNCATEGORISED) return -1
      return 0
    })
}

export const EligibilityRules = ({
  productName,
  rules,
}: {
  productName: string
  rules: IProductEligibilityRule[]
}) => {
  const groupedRules = useMemo(() => groupByCategory(rules), [rules])

  if (rules.length === 0) {
    return <EmptyMessage>No eligibility rules are configured for {productName}.</EmptyMessage>
  }

  return (
    <>
      {groupedRules.map(([category, categoryRules], index) => (
        <Fragment key={category}>
          {index > 0 && <StyledDivider />}
          <Box py={1}>
            <CategoryTitle>{category}</CategoryTitle>
            <DataTable<IProductEligibilityRule> rows={categoryRules} columns={ruleColumns} />
          </Box>
        </Fragment>
      ))}
    </>
  )
}

const StyledDivider = styled(Divider)(() => ({
  borderColor: '#EAECF0',
  padding: '10px 0',
}))

const CategoryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'Inter',
  fontWeight: 600,
  color: '#344054',
  textTransform: 'capitalize',
  marginLeft: '10px',
  marginBottom: '8px',
  [theme.breakpoints.down('md')]: {
    marginLeft: '0px',
  },
}))

const StyledText = styled(Typography)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
})

const StrongText = styled(StyledText)({
  fontWeight: 600,
  color: '#101828',
})

const EmptyMessage = styled(Typography)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#98A2B3',
})
